/**
 * ConvoCX — Astryx app entry (CON-832 / CON-845).
 *
 * Registers the convocx brand theme and mounts the branded app.
 * Light is the product default (PRD D8); dark is an opt-in override via
 * the top-nav toggle. The mode is owned in React state and passed to the
 * root <Theme mode> prop — that is what drives both <html data-theme> and
 * the wrapper's color-scheme (which resolves the light-dark() tokens).
 *
 * LayerProvider hosts the toast viewport used by notifications.
 */
import React, { useState } from 'react';
import ReactDOM from 'react-dom/client';
import { Theme, LayerProvider } from '@astryxdesign/core';
import { convocx, CONVOX_DEFAULT_THEME_MODE, registerConvocxTheme } from './theme/convocx';
import { initErrorForwarder } from './telemetry/errorForwarder';
import { AuthProvider } from './inbox/auth';
import { App } from './App';
import './theme/design-tokens.css';
import '@astryxdesign/core/reset.css';
import '@astryxdesign/core/astryx.css';

registerConvocxTheme();
initErrorForwarder();

type ThemeMode = 'dark' | 'light';

function ThemeRoot() {
  const [mode, setMode] = useState<ThemeMode>(CONVOX_DEFAULT_THEME_MODE);

  const toggleMode = () => {
    setMode((prev) => (prev === 'dark' ? 'light' : 'dark'));
  };

  return (
    <Theme theme={convocx} mode={mode}>
      <LayerProvider>
        <AuthProvider>
          <App mode={mode} onToggleMode={toggleMode} />
        </AuthProvider>
      </LayerProvider>
    </Theme>
  );
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ThemeRoot />
  </React.StrictMode>,
);